import { useContext, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { makeCancelTokenSource, getApiClient, handleApiError } from '../api_client/get';
import { useBranchIO } from '../contexts/branch_io_provider';
import CartContext from '../contexts/cart';
import ReferralContext from '../contexts/referral';

// This function determines the referral code based on the host.
// We have different rules for different hosts. A user can enter the
// app from a branch.io link or from a joinpipp.com link.
const extractReferralCode = (hostname, pathname, search) => {
  // This may happen in certain situations (localhost)
  if (hostname == null) hostname = process.env.REACT_APP_CANONICAL_HOST;

  if (!pathname) return null;
  switch (hostname) {
    case process.env.REACT_APP_CANONICAL_HOST:
      return (new URLSearchParams(search)).get('r') || null;
    case process.env.REACT_APP_DEFAULT_BRANCHIO_LINK_DOMAIN:
    case process.env.REACT_APP_DEFAULT_BRANCHIO_ALT_LINK_DOMAIN: {
      const code = pathname.startsWith('/') ? pathname.substring(1) : pathname;
      return code || null;
    }
    default:
      return null;
  }
};

const ReferralHandler = () => {
  const location = useLocation();
  const currentHostname = location.hostname;
  const currentPathname = location.pathname;
  const currentSearch = location.search;
  const referralContainer = useContext(ReferralContext);
  const cart = useContext(CartContext);
  const { data: branchData } = useBranchIO();

  const referralCode = useMemo(() => {
    // First, we attempt to use the referral from the standard link system
    const candidateReferral = extractReferralCode(currentHostname, currentPathname, currentSearch);
    if (candidateReferral) return candidateReferral;
    // If no candidate is found, we try branch.
    let parsedBranchData = branchData?.data_parsed ||
    (typeof branchData?.data === 'object' ? branchData.data : null);
    if (!parsedBranchData && typeof branchData?.data === 'string') {
      try {
        parsedBranchData = JSON.parse(branchData.data);
      } catch (e) {
        parsedBranchData = null;
      }
    }
    if (parsedBranchData && parsedBranchData['~referring_link']) {
      const url = new URL(parsedBranchData['~referring_link']);
      const code = extractReferralCode(url.hostname, url.pathname, url.search);
      if (code) return code;
    }
    return null;
  }, [currentHostname, currentPathname, currentSearch, branchData]);

  useEffect(() => {
    if (!referralCode) return;
    let unmounted = false;
    const cancelTokenSource = makeCancelTokenSource();
    referralContainer.set(referralCode);
    getApiClient().handleReferral(referralCode, cancelTokenSource)
      .then(({ data }) => {
        if (unmounted) return;
        if (data.success) {
          // It is possible that a referral affects the cart and pricing.
          // For example, being referred by Ghina Ghandour can undo promo codes
          // in certain situations.
          console.log('Referral ' + referralCode + ' handled.');
          cart.setElementsAndPricing(data.cart, data.cart_pricing);
        } else {
          console.log('Referral failed.');
        }
      })
      .catch(error => {
        if (unmounted) return;
        console.log('Failed to handle referral ' + referralCode);
        handleApiError(error, console.log);
      });
    return () => {
      unmounted = true;
      cancelTokenSource.cancel();
    };
  }, [referralCode, cart, referralContainer]);

  return null;
};

export default ReferralHandler;
